import { useCallback, useEffect, useMemo, useState } from "react";
import { Smartphone, Users, Wifi, WifiOff, Activity, AlertTriangle, Radio } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listPhones, type Phone } from "../api/phones";
import { listAccounts, type AccountStats } from "../api/accounts";
import { type RealtimeEvent, useRealtimeChannel } from "../hooks/useRealtimeChannel";

interface PhoneStats {
  total: number;
  online: number;
  busy: number;
  offline: number;
  error: number;
}

function emptyPhoneStats(): PhoneStats {
  return { total: 0, online: 0, busy: 0, offline: 0, error: 0 };
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const [phones, setPhones] = useState<Phone[]>([]);
  const [accountStats, setAccountStats] = useState<AccountStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setError(null);
        const [p, a] = await Promise.all([listPhones(), listAccounts({ limit: 1 })]);
        if (cancelled) return;
        setPhones(p);
        setAccountStats(a.stats);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("common.error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    // Fallback : refresh complet toutes les 60s pour récupérer les comptes nouvellement créés
    // (les events WS gèrent le live, ce refresh corrige les dérives potentielles).
    // NB : on nomme la variable `iv` (pas `t`) pour ne pas shadow le `t` de useTranslation.
    const iv = setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [t]);

  // Realtime : on update les statuts des tels et incrémente le compteur d'accounts
  const rooms = useMemo(() => ["phones"], []);
  const onEvent = useCallback((evt: RealtimeEvent) => {
    if (evt.event === "phone:update") {
      const p = evt.payload;
      setPhones((cur) =>
        cur.map((ph) =>
          ph.id === p.phone_id
            ? {
                ...ph,
                status: p.status ?? ph.status,
                computed_status: (p.status ?? ph.computed_status) as Phone["computed_status"],
                last_heartbeat_at: p.last_heartbeat_at ?? ph.last_heartbeat_at,
              }
            : ph
        )
      );
    } else if (evt.event === "account:created") {
      setAccountStats((s) => (s ? { ...s, total: s.total + 1, active: s.active + 1 } : s));
    }
  }, []);
  const { connected } = useRealtimeChannel(rooms, onEvent);

  const phoneStats = phones.reduce<PhoneStats>((acc, p) => {
    acc.total++;
    // Un tel en pause (run paused) est compté comme busy au niveau dashboard.
    const key = p.computed_status === "paused" ? "busy" : p.computed_status;
    acc[key]++;
    return acc;
  }, emptyPhoneStats());

  return (
    <div className="p-8">
      <header className="mb-10 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("dashboard.title")}</h1>
          <p className="mt-2 text-ink-500">{t("dashboard.subtitle")}</p>
        </div>
        <div
          className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${
            connected
              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
              : "bg-ink-700/40 text-ink-500 border-ink-600"
          }`}
        >
          <Radio className={`size-3 ${connected ? "animate-pulse" : ""}`} />
          {connected ? t("dashboard.realtimeOn") : t("dashboard.realtimeOff")}
        </div>
      </header>

      {error && (
        <div className="mb-6 p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <section className="mb-10">
        <h2 className="text-sm font-semibold text-ink-500 uppercase tracking-wider mb-3">
          {t("dashboard.sectionPhones")}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Smartphone} label={t("dashboard.cards.total")} value={phoneStats.total} loading={loading} />
          <StatCard icon={Wifi} label={t("dashboard.cards.online")} value={phoneStats.online} loading={loading} color="emerald" />
          <StatCard icon={Activity} label={t("dashboard.cards.busy")} value={phoneStats.busy} loading={loading} color="amber" />
          <StatCard icon={WifiOff} label={t("dashboard.cards.offline")} value={phoneStats.offline} loading={loading} color="ink" />
          <StatCard icon={AlertTriangle} label={t("dashboard.cards.error")} value={phoneStats.error} loading={loading} color="red" />
        </div>
        <Link
          to="/phones"
          className="inline-block mt-3 text-sm text-velvet-400 hover:text-velvet-300"
        >
          {t("dashboard.seeAllPhones")}
        </Link>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-ink-500 uppercase tracking-wider mb-3">
          {t("dashboard.sectionAccounts")}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Users} label={t("dashboard.cards.total")} value={accountStats?.total ?? 0} loading={loading} />
          <StatCard icon={Users} label={t("dashboard.cards.active")} value={accountStats?.active ?? 0} loading={loading} color="emerald" />
          <StatCard icon={Users} label={t("dashboard.cards.suspended")} value={accountStats?.suspended ?? 0} loading={loading} color="amber" />
          <StatCard icon={Users} label={t("dashboard.cards.deleted")} value={accountStats?.deleted ?? 0} loading={loading} color="red" />
          <StatCard icon={Users} label={t("dashboard.cards.pendingReview")} value={accountStats?.pending_validation ?? 0} loading={loading} color="sky" />
        </div>
        <Link
          to="/accounts"
          className="inline-block mt-3 text-sm text-velvet-400 hover:text-velvet-300"
        >
          {t("dashboard.seeAllAccounts")}
        </Link>
      </section>
    </div>
  );
}

const COLOR_MAP: Record<string, string> = {
  velvet: "text-velvet-400",
  emerald: "text-emerald-400",
  amber: "text-amber-400",
  red: "text-red-400",
  sky: "text-sky-400",
  ink: "text-ink-500",
};

function StatCard({
  icon: Icon,
  label,
  value,
  loading,
  color = "velvet",
}: {
  icon: typeof Smartphone;
  label: string;
  value: number;
  loading: boolean;
  color?: keyof typeof COLOR_MAP;
}): JSX.Element {
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-4 hover:border-velvet-600/50 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-sm text-ink-500">{label}</span>
        <Icon className={`size-4 ${COLOR_MAP[color]}`} />
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">
        {loading ? <span className="text-ink-500">···</span> : value}
      </div>
    </div>
  );
}
